/*
 * Library for encoding and decoding a JWT/JWE token
 * This is used for securing all communication between the web client (SPA)
 * and the express server that works as middelware between the web-client and
 * database backend - (dGraph)
 * The idea is that this library is to be used by both the client and the server.
 * (c) Hypatia Learning AS, Erlend Øverby
 */

import { encodeLoad, decodeLoad } from '@/lib/base64'

const JWE_ALG = 'RSA-OAEP'
const JWE_ENC = 'A128CBC-HS256'
const JWT_ISSUER = 'Hypatia Learning AS'
const JWT_AUDIENCE = 'PROCLUS'
const JWT_EXPIRATION_SECONDS = 16 * 60 * 60 // 16 hours (in seconds)
const RSA_ENCRYPTION_ALGORITHM_IDENTIFIER = new Uint8Array([
  0x30, 0x0d, 0x06, 0x09, 0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x01, 0x01,
  0x05, 0x00
])

const textEncoder = new TextEncoder()
const textDecoder = new TextDecoder()

export class CreateHypatiaJWE {
  /*
   * Payload: datastructure to be part of signed token
   * Secret: secret used to encode and encrypt the package
   */
  constructor(payload, secret, privateKey, publicKey) {
    /*
     * privateKey is used for decryption -- in the express server
     * publicKey is used for encrypting  -- in the web-client
     */
    console.log('------------------------ Constructor : (' + secret + ')')
    this.payload = payload
    this.secret = secret
    this.privateKey = privateKey
    this.publicKey = publicKey
    this.keyPrivateKey = null
    this.keyPublicKey = null
    this.keyId = null
  }

  async setUp() {
    try {
      if (this.publicKey !== null && this.publicKey !== undefined) {
        this.keyPublicKey = await importRsaPublicKey(this.publicKey)
        this.keyId = await createRsaPublicKeyThumbprint(this.keyPublicKey)
      }

      if (this.privateKey !== null && this.privateKey !== undefined) {
        console.log('======= PRIVATE KEY ==========')
        this.keyPrivateKey = await importRsaPrivateKey(this.privateKey)
      }

      return 'OK'
    } catch (error) {
      console.error('*** ERROR CREATE CRYPTO KEYS *** : ' + error)
      throw new Error('*** ERROR CREATE CRYPTO KEYS ***', { cause: error })
    }
  }

  async encrypt(rawData) {
    if (!this.keyPublicKey) {
      throw new Error('Missing a public Key !')
    }

    const cek = getCrypto().getRandomValues(new Uint8Array(32))
    const iv = getCrypto().getRandomValues(new Uint8Array(16))
    const protectedHeader = {
      enc: JWE_ENC,
      alg: JWE_ALG
    }

    if (this.keyId) {
      protectedHeader.kid = this.keyId
    }

    const protectedEncoded = base64UrlEncodeString(
      JSON.stringify(protectedHeader)
    )
    const encryptedKey = await getCrypto().subtle.encrypt(
      { name: 'RSA-OAEP' },
      this.keyPublicKey,
      cek
    )
    const encryptedContent = await encryptA128CbcHs256(
      stringToBytes(rawData),
      cek,
      iv,
      stringToBytes(protectedEncoded)
    )

    return encodeLoad({
      recipients: [
        {
          encrypted_key: base64UrlEncode(encryptedKey)
        }
      ],
      protected: protectedEncoded,
      iv: base64UrlEncode(iv),
      ciphertext: base64UrlEncode(encryptedContent.ciphertext),
      tag: base64UrlEncode(encryptedContent.tag)
    })
  }

  async sign(payload, serverSecret) {
    if (typeof serverSecret !== 'string' || serverSecret.length === 0) {
      throw new Error('serverSecret must be a non-empty string')
    }
    if (!payload || typeof payload !== 'object') {
      throw new Error('payload must be an object')
    }

    try {
      const now = Math.floor(Date.now() / 1000)
      const header = {
        alg: 'HS256',
        typ: 'JWT'
      }
      const jwtPayload = {
        ...payload,
        iat: now,
        iss: JWT_ISSUER,
        aud: JWT_AUDIENCE,
        exp: now + JWT_EXPIRATION_SECONDS
      }
      const signingInput =
        base64UrlEncodeString(JSON.stringify(header)) +
        '.' +
        base64UrlEncodeString(JSON.stringify(jwtPayload))
      const secretKey = await getCrypto().subtle.importKey(
        'raw',
        stringToBytes(serverSecret),
        {
          name: 'HMAC',
          hash: 'SHA-256'
        },
        false,
        ['sign']
      )
      const signature = await getCrypto().subtle.sign(
        'HMAC',
        secretKey,
        stringToBytes(signingInput)
      )
      const encodedSignature = base64UrlEncode(signature)

      if (!encodedSignature) {
        throw new Error('Failed to encode JWT signature')
      }

      return signingInput + '.' + encodedSignature
    } catch (error) {
      console.error('CreateHypatiaJWE.sign error:', error)
      throw new Error('Failed to sign JWT', { cause: error })
    }
  }

  async decrypt(encryptedMsg) {
    console.log('======= DECRYPT =======')

    if (!encryptedMsg) {
      throw new Error('Missing data to decrypt !')
    }
    if (!this.keyPrivateKey) {
      throw new Error('Missing a private Key !')
    }

    try {
      const decoded = decodeLoad(encryptedMsg)
      const jwe = typeof decoded === 'string' ? JSON.parse(decoded) : decoded
      const recipient = jwe.recipients?.[0]

      if (!recipient?.encrypted_key) {
        throw new Error('Missing encrypted key !')
      }

      const cek = await getCrypto().subtle.decrypt(
        { name: 'RSA-OAEP' },
        this.keyPrivateKey,
        base64UrlDecode(recipient.encrypted_key)
      )
      const plaintext = await decryptA128CbcHs256(
        base64UrlDecode(jwe.ciphertext),
        cek,
        base64UrlDecode(jwe.iv),
        stringToBytes(jwe.protected),
        base64UrlDecode(jwe.tag)
      )

      return {
        plaintext,
        payload: plaintext,
        protectedHeader: JSON.parse(
          bytesToString(base64UrlDecode(jwe.protected))
        )
      }
    } catch (error) {
      console.error('**** DECRYPT *** had some problems !' + error)
      throw new Error('**** DECRYPT *** had some problems !', { cause: error })
    }
  }
}

async function encryptA128CbcHs256(plaintext, cek, iv, aad) {
  const cekBytes = new Uint8Array(cek)
  const macKey = cekBytes.slice(0, 16)
  const encKey = cekBytes.slice(16)
  const aesKey = await getCrypto().subtle.importKey(
    'raw',
    encKey,
    { name: 'AES-CBC' },
    false,
    ['encrypt']
  )
  const ciphertext = new Uint8Array(
    await getCrypto().subtle.encrypt(
      {
        name: 'AES-CBC',
        iv
      },
      aesKey,
      plaintext
    )
  )
  const tag = await createA128CbcHs256Tag(macKey, aad, iv, ciphertext)

  return {
    ciphertext,
    tag
  }
}

async function decryptA128CbcHs256(ciphertext, cek, iv, aad, tag) {
  const cekBytes = new Uint8Array(cek)
  const macKey = cekBytes.slice(0, 16)
  const encKey = cekBytes.slice(16)
  const expectedTag = await createA128CbcHs256Tag(macKey, aad, iv, ciphertext)

  if (!constantTimeEqual(expectedTag, tag)) {
    throw new Error('mac check failed')
  }

  const aesKey = await getCrypto().subtle.importKey(
    'raw',
    encKey,
    { name: 'AES-CBC' },
    false,
    ['decrypt']
  )

  return new Uint8Array(
    await getCrypto().subtle.decrypt(
      {
        name: 'AES-CBC',
        iv
      },
      aesKey,
      ciphertext
    )
  )
}

async function createA128CbcHs256Tag(macKey, aad, iv, ciphertext) {
  const hmacKey = await getCrypto().subtle.importKey(
    'raw',
    macKey,
    {
      name: 'HMAC',
      hash: 'SHA-256'
    },
    false,
    ['sign']
  )
  const macData = concatBytes(aad, iv, ciphertext, uint64be(aad.length * 8))
  const mac = new Uint8Array(
    await getCrypto().subtle.sign('HMAC', hmacKey, macData)
  )

  return mac.slice(0, 16)
}

async function importRsaPublicKey(pem) {
  const der = pemToDer(pem)

  return getCrypto().subtle.importKey(
    'spki',
    der.label === 'RSA PUBLIC KEY' ? wrapPkcs1PublicKey(der.bytes) : der.bytes,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-1'
    },
    true,
    ['encrypt']
  )
}

async function importRsaPrivateKey(pem) {
  const der = pemToDer(pem)

  return getCrypto().subtle.importKey(
    'pkcs8',
    der.label === 'RSA PRIVATE KEY'
      ? wrapPkcs1PrivateKey(der.bytes)
      : der.bytes,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-1'
    },
    false,
    ['decrypt']
  )
}

async function createRsaPublicKeyThumbprint(publicKey) {
  const jwk = await getCrypto().subtle.exportKey('jwk', publicKey)
  const thumbprintJson = JSON.stringify({
    e: jwk.e,
    kty: 'RSA',
    n: jwk.n
  })
  const digest = await getCrypto().subtle.digest(
    'SHA-256',
    stringToBytes(thumbprintJson)
  )

  return base64UrlEncode(digest)
}

function pemToDer(pem) {
  const label = pem.match(/-----BEGIN ([^-]+)-----/)?.[1]
  const base64 = pem.replace(/-----[^-]+-----/g, '').replace(/\s/g, '')

  return {
    label,
    bytes: base64ToBytes(base64)
  }
}

function wrapPkcs1PublicKey(pkcs1Der) {
  return derSequence(
    RSA_ENCRYPTION_ALGORITHM_IDENTIFIER,
    derBitString(pkcs1Der)
  )
}

function wrapPkcs1PrivateKey(pkcs1Der) {
  return derSequence(
    new Uint8Array([0x02, 0x01, 0x00]),
    RSA_ENCRYPTION_ALGORITHM_IDENTIFIER,
    derOctetString(pkcs1Der)
  )
}

function derSequence(...parts) {
  return derEncode(0x30, concatBytes(...parts))
}

function derBitString(value) {
  return derEncode(0x03, concatBytes(new Uint8Array([0x00]), value))
}

function derOctetString(value) {
  return derEncode(0x04, value)
}

function derEncode(tag, value) {
  return concatBytes(new Uint8Array([tag]), derLength(value.length), value)
}

function derLength(length) {
  if (length < 128) {
    return new Uint8Array([length])
  }

  const bytes = []
  let remaining = length

  while (remaining > 0) {
    bytes.unshift(remaining & 0xff)
    remaining = remaining >> 8
  }

  return new Uint8Array([0x80 | bytes.length, ...bytes])
}

function base64UrlEncodeString(value) {
  return base64UrlEncode(stringToBytes(value))
}

function base64UrlEncode(value) {
  const bytes = new Uint8Array(value)
  let binary = ''

  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '')
}

function base64UrlDecode(value) {
  return base64ToBytes(
    value.replace(/-/g, '+').replace(/_/g, '/') + base64Padding(value)
  )
}

function base64ToBytes(value) {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }

  return bytes
}

function base64Padding(value) {
  const paddingLength = (4 - (value.length % 4)) % 4

  return '='.repeat(paddingLength)
}

function stringToBytes(value) {
  return textEncoder.encode(value)
}

function bytesToString(value) {
  return textDecoder.decode(value)
}

function concatBytes(...parts) {
  const length = parts.reduce((total, part) => total + part.length, 0)
  const result = new Uint8Array(length)
  let offset = 0

  for (const part of parts) {
    result.set(part, offset)
    offset += part.length
  }

  return result
}

function uint64be(value) {
  const result = new Uint8Array(8)
  let remaining = value

  for (let i = 7; i >= 0; i--) {
    result[i] = remaining & 0xff
    remaining = Math.floor(remaining / 256)
  }

  return result
}

function constantTimeEqual(left, right) {
  if (left.length !== right.length) {
    return false
  }

  let mismatch = 0

  for (let i = 0; i < left.length; i++) {
    mismatch |= left[i] ^ right[i]
  }

  return mismatch === 0
}

function getCrypto() {
  if (!globalThis.crypto?.subtle) {
    throw new Error('Web Crypto API is not available')
  }

  return globalThis.crypto
}
